import { MerchantAgreementStatus } from './MerchantAgreement';

export interface MerchantAddress {
    addressLine1: string;
    addressLine2?: string;
    coAddress?: string;
    city: string;
    postalCode: string;
    countryCode: string;
}

export interface MerchantContactInformation {
    name: string;
    address: MerchantAddress;
    phone: string;
    email: string;
    websiteUrl: string;
}

export interface MerchantInvoiceInformation {
    address: MerchantAddress;
    email: string;
}

export enum MerchantStatus {
    /** Merchant created but not yet approved. */
    Pending = 'Pending',
    /** Merchant is approved and ready. */
    Approved = 'Approved',
}

export const merchantStatuses: MerchantStatus[] = Object.values(MerchantStatus);

/** State of the merchant's KYC application (Bits), updated by the Bits webhook. */
export enum MerchantKycStatus {
    /** No KYC application exists for the merchant (added with an override), so the KYC workflow cannot be opened. */
    NotAvailable = 'NotAvailable',
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    InReview = 'InReview',
    Approved = 'Approved',
    Declined = 'Declined',
}

/** State of the merchant's bank account verification (a Bits workflow of its own), updated by the Bits webhook. */
export enum MerchantBankAccountStatus {
    /** No bank account verification exists for the merchant, so the workflow cannot be opened. */
    NotAvailable = 'NotAvailable',
    NotStarted = 'NotStarted',
    Pending = 'Pending',
    Verified = 'Verified',
    Failed = 'Failed',
}

/**
 * The Bits workflows a merchant goes through. The values are the route value of
 * POST organizations/{id}/merchants/{countryCode}/{organizationNumber}/workflows/{workflow}/session.
 */
export enum MerchantWorkflow {
    Kyc = 'kyc',
    BankAccount = 'bankAccount',
}

/** A merchant (legal entity) connected to an organization, as returned by GET organizations/{id}/merchants. */
export interface Merchant {
    organizationNumber: string;
    countryCode: string;
    /** Legal name. Empty until KYC has completed and the Bits sync has populated it. */
    name: string;
    vatRegistrationCode: string;
    /** Missing on merchants created before the status was introduced; treated as Pending. */
    status?: MerchantStatus;
    /**
     * Where the merchant agreement stands. KYC and bank account verification are NotAvailable until it is signed.
     * Missing on merchants returned before agreements were introduced; treated as NotSent.
     */
    agreementStatus?: MerchantAgreementStatus;
    kycStatus: MerchantKycStatus;
    bankAccountStatus: MerchantBankAccountStatus;
    contactInformation: MerchantContactInformation;
    invoiceInformation: MerchantInvoiceInformation;
    /**
     * The https URLs a store of the merchant may be onboarded on; the store URL is picked from these.
     * Missing on merchants returned before the list was introduced; treated as empty.
     */
    allowedUrls?: string[];
    /** The markets (country codes) a store of the merchant may be onboarded in. Missing when the merchant is not restricted. */
    allowedMarkets?: string[];
    /** The sales segments (B2C or B2B) a store of the merchant may be onboarded in. Missing when the merchant is not restricted. */
    allowedSegments?: string[];
    /**
     * The template of the merchant, from the key of its signup link: the onboarding template its stores are onboarded
     * from (see getStoreTemplate in OnboardStore) and the agreement template it signs. Missing when the merchant has the
     * defaults.
     */
    template?: string;
}

/** Body of POST organizations/{id}/merchants: the merchant fields collected at organization onboarding. */
export interface AddMerchantRequest {
    countryCode: string;
    organizationNumber: string;
    /** Legal name of the merchant, used when it has to be created in KYC. */
    name: string;
    /** The https URLs a store of the merchant may be onboarded on. Empty when none were given. */
    allowedUrls: string[];
}

/**
 * The fields of a merchant that can be edited after it was added, each left out when it is not to be changed. Mirrors
 * PatchMerchant.Changes in the API: a new editable field is a property here, a section in EditMerchantForm and a
 * property of Changes in the API.
 */
export interface MerchantChanges {
    /** The https URLs a store of the merchant may be onboarded on, replacing the current ones. */
    allowedUrls?: string[];
}

/** A JSON patch operation, as PATCH organizations/{id}/merchants/{countryCode}/{organizationNumber} takes them. */
export interface MerchantPatchOperation {
    op: 'replace';
    path: string;
    value: unknown;
}

/** The JSON patch for the changes: one replace per field given, so a field left out is not touched. */
export const toMerchantPatch = (changes: MerchantChanges): MerchantPatchOperation[] =>
    Object.entries(changes)
        .filter(([, value]) => value !== undefined)
        .map(([field, value]) => ({ op: 'replace', path: `/${field}`, value }));

/** Whether the changes change anything at all. */
export const hasMerchantChanges = (changes: MerchantChanges): boolean =>
    toMerchantPatch(changes).length > 0;

/** The URLs a store of the merchant may be onboarded on. */
export const getAllowedUrls = (merchant: Merchant): string[] => merchant.allowedUrls ?? [];

/** Whether a store of the merchant may be onboarded in the market; any market when the merchant has no allowed markets. */
export const isAllowedMarket = (merchant: Merchant, countryCode: string): boolean =>
    !merchant.allowedMarkets ||
    merchant.allowedMarkets.some(market => market.toUpperCase() === countryCode?.toUpperCase());

/** Whether a store of the merchant may be onboarded in the segment; any segment when the merchant has no allowed segments. */
export const isAllowedSegment = (merchant: Merchant, segment: string): boolean =>
    !merchant.allowedSegments ||
    merchant.allowedSegments.some(allowed => allowed.toUpperCase() === segment?.toUpperCase());

/** Stable key for a merchant. Country code + organization number identify a merchant within an organization. */
export const getMerchantKey = (merchant: Merchant) =>
    `${merchant.countryCode}-${merchant.organizationNumber}`;

/** Status to show for a merchant. A merchant without a status has not been approved yet. */
export const getMerchantStatus = (merchant: Merchant): MerchantStatus =>
    merchant.status ?? MerchantStatus.Pending;

/** Where the agreement of the merchant stands. A merchant without the field has not had one sent. */
export const getAgreementStatus = (merchant: Merchant): MerchantAgreementStatus =>
    merchant.agreementStatus ?? MerchantAgreementStatus.NotSent;

/** Whether the agreement is signed, in Scrive or before the merchant was added. */
export const isAgreementSigned = (merchant: Merchant): boolean =>
    getAgreementStatus(merchant) === MerchantAgreementStatus.Signed ||
    getAgreementStatus(merchant) === MerchantAgreementStatus.AlreadySigned;

/** Whether the merchant name has been set (by KYC). */
export const hasMerchantName = (merchant: Merchant): boolean =>
    (merchant.name ?? '').trim().length > 0;

/** Whether the merchant has a KYC application, i.e. the KYC workflow can be opened and its state shown. */
export const hasKyc = (merchant: Merchant): boolean =>
    merchant.kycStatus !== MerchantKycStatus.NotAvailable;

/**
 * Whether KYC is done for the merchant: only when Bits has approved the KYC application. The merchant status is
 * independent of it; an internal user may approve a merchant before (or without) its KYC being finished.
 */
export const isKycComplete = (merchant: Merchant): boolean =>
    merchant.kycStatus === MerchantKycStatus.Approved;

/** Whether the merchant has a bank account verification, i.e. the workflow can be opened and its state shown. */
export const hasBankAccountVerification = (merchant: Merchant): boolean =>
    merchant.bankAccountStatus !== MerchantBankAccountStatus.NotAvailable;

/** Whether the merchant's bank accounts are verified. */
export const isBankAccountVerified = (merchant: Merchant): boolean =>
    merchant.bankAccountStatus === MerchantBankAccountStatus.Verified;

/** Whether the merchant has the given workflow, i.e. it can be opened and its state shown. */
export const hasWorkflow = (merchant: Merchant, workflow: MerchantWorkflow): boolean =>
    workflow === MerchantWorkflow.Kyc ? hasKyc(merchant) : hasBankAccountVerification(merchant);

/** Whether the given workflow is done for the merchant. */
export const isWorkflowComplete = (merchant: Merchant, workflow: MerchantWorkflow): boolean =>
    workflow === MerchantWorkflow.Kyc ? isKycComplete(merchant) : isBankAccountVerified(merchant);
